/**
 * Scoped Persistence Migration
 *
 * CONTEXT:
 * Prior to scoped persistence, all redux-persist keys were global (shared across users).
 * After scoped persistence, keys are namespaced by environment/org/user to prevent data bleed.
 *
 * This migration runs ONCE per user on first authenticated app load to transfer
 * legacy unscoped data to the new scoped format.
 *
 * CREATED: 2025-11-11
 * REMOVE_AFTER: 2026-01-11 (60 days after initial release)
 *
 * Why 60 days?
 * - Multi-user devices need time for all users to login at least once
 * - Longer than typical sprint cycles for thorough validation
 * - Accounts for holiday periods and infrequent users
 *
 * MIGRATION BEHAVIOR:
 * - Discovers ALL legacy persist keys dynamically (not hardcoded)
 * - Migrates root state and all feature slices automatically
 * - Special handling: clears activeWorkflowEncounterId (prevents stale session state)
 * - All other persisted state migrates with data intact
 * - Idempotent: safe to run multiple times, only migrates once per user
 *
 * @module store/migrations/scopedPersistMigration
 */

import * as Sentry from '@sentry/react';

import { getLegacyKey, getScopedKey, type Owner } from '../owner';

/**
 * Discovers all legacy persist keys in localStorage that need migration
 * Returns keys without the "persist:" prefix for easier handling
 */
function discoverLegacyPersistKeys(): string[] {
  const legacyKeys: string[] = [];

  // Iterate through all localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const fullKey = localStorage.key(i);
    if (!fullKey) continue;

    // Match legacy persist keys: persist:* but NOT scoped keys (persist:env:user:*)
    if (fullKey.startsWith('persist:')) {
      // Extract the part after "persist:"
      const keyPart = fullKey.substring('persist:'.length);

      // Skip if this is a scoped key (contains owner prefix pattern: env:userId:)
      // Scoped keys have at least 2 colons before the actual key
      const colonCount = (keyPart.match(/:/g) || []).length;
      if (colonCount >= 2) {
        // This is already a scoped key, skip
        continue;
      }

      // Skip migration flag keys
      if (keyPart.startsWith('migration-scoped:')) {
        continue;
      }

      // This is a legacy key - add the key part (without "persist:" prefix)
      legacyKeys.push(keyPart);
    }
  }

  return legacyKeys;
}

/**
 * Get migration flag key (per-user to support multi-user devices)
 */
function getMigrationFlagKey(owner: Owner): string {
  return `persist:migration-scoped:${owner.userId}`;
}

/**
 * Run the scoped persistence migration for a specific user
 *
 * This function:
 * 1. Checks if migration already ran for this user (idempotent)
 * 2. Reads all legacy unscoped persist keys from localStorage
 * 3. Copies data to new scoped keys
 * 4. Deletes legacy keys
 * 5. Marks migration as complete for this user
 *
 * @param owner - Current authenticated user's owner context
 */
export function runScopedPersistMigration(owner: Owner): void {
  try {
    // Check if migration already ran for this user
    const flagKey = getMigrationFlagKey(owner);
    const migrated = localStorage.getItem(flagKey);

    if (migrated === 'true') {
      // Migration already completed for this user - silent return
      return;
    }

    let migratedCount = 0;
    let errorCount = 0;

    // Discover all legacy persist keys dynamically
    const legacySliceKeys = discoverLegacyPersistKeys();

    if (process.env.NODE_ENV !== 'production') {
      console.log(`[Migration] Discovered ${legacySliceKeys.length} legacy keys to migrate:`, legacySliceKeys);
    }

    // Migrate each discovered slice
    for (const sliceKey of legacySliceKeys) {
      try {
        const legacyKey = getLegacyKey(sliceKey);
        const scopedKey = getScopedKey(owner, sliceKey);

        // Read legacy value
        const legacyValue = localStorage.getItem(legacyKey);

        if (!legacyValue) {
          // No legacy data for this key - skip
          continue;
        }

        // Check if scoped key already has data (shouldn't happen, but safety check)
        const existingScopedValue = localStorage.getItem(scopedKey);
        if (existingScopedValue) {
          // Scoped key already populated - skip to avoid overwriting
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              `[Migration] Skipping ${sliceKey} - scoped key already has data`
            );
          }
          continue;
        }

        // Validate JSON structure
        try {
          JSON.parse(legacyValue);
        } catch {
          console.warn(`[Migration] Invalid JSON in legacy key ${legacyKey}, skipping`);
          errorCount++;
          continue;
        }

        // Skip workflow slice entirely - session data should not persist across migrations
        if (sliceKey === 'feature_encounters/workflow_encounters/workflow') {
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[Migration] Skipping workflow state (session-specific data)`);
          }
          // Delete legacy key but don't migrate
          localStorage.removeItem(legacyKey);
          continue;
        }

        // Copy value to scoped key
        localStorage.setItem(scopedKey, legacyValue);

        // Delete legacy key
        localStorage.removeItem(legacyKey);

        migratedCount++;

        if (process.env.NODE_ENV !== 'production') {
          console.log(`[Migration] Migrated ${sliceKey}:`, {
            from: legacyKey,
            to: scopedKey,
          });
        }
      } catch (error) {
        console.error(`[Migration] Error migrating ${sliceKey}:`, error);
        errorCount++;
        // Continue with other keys
      }
    }

    // Mark migration as complete for this user
    localStorage.setItem(flagKey, 'true');

    // Log completion
    if (migratedCount > 0 || errorCount > 0) {
      /* eslint-disable no-console */
      console.log(
        '✅ [Migration] Scoped persistence migration completed',
        {
          user: owner.userId,
          env: owner.env,
          migrated: migratedCount,
          errors: errorCount,
        }
      );
      /* eslint-enable no-console */

      // Log to Sentry for monitoring
      Sentry.captureMessage('Scoped persistence migration completed', {
        level: 'info',
        tags: {
          migration: 'scoped-persistence',
          env: owner.env,
        },
        extra: {
          userId: owner.userId,
          migratedCount,
          errorCount,
        },
      });
    }
  } catch (error) {
    console.error('[Migration] Critical error during scoped persistence migration:', error);

    // Log error but don't break app initialization
    Sentry.captureException(error, {
      tags: {
        migration: 'scoped-persistence',
        env: owner.env,
      },
      extra: {
        userId: owner.userId,
      },
    });

    // Mark as complete even on error to avoid retry loops
    const flagKey = getMigrationFlagKey(owner);
    localStorage.setItem(flagKey, 'true');
  }
}

/**
 * Utility function to check migration status for current user (for debugging)
 * Available in browser console: window.__checkScopedPersistMigration()
 */
export function checkMigrationStatus(owner: Owner | null): void {
  if (!owner) {
    /* eslint-disable no-console */
    console.log('=== Scoped Persistence Migration Status ===');
    console.log('No owner available (not authenticated)');
    console.log('===========================================');
    /* eslint-enable no-console */
    return;
  }

  const flagKey = getMigrationFlagKey(owner);
  const migrated = localStorage.getItem(flagKey);

  // Discover current legacy keys (if any remain)
  const currentLegacyKeys = discoverLegacyPersistKeys();

  const legacyKeys: Record<string, string | null> = {};
  const scopedKeys: Record<string, string | null> = {};

  // Check all discovered keys
  for (const sliceKey of currentLegacyKeys) {
    const legacyKey = getLegacyKey(sliceKey);
    const scopedKey = getScopedKey(owner, sliceKey);

    legacyKeys[sliceKey] = localStorage.getItem(legacyKey);
    scopedKeys[sliceKey] = localStorage.getItem(scopedKey);
  }

  /* eslint-disable no-console */
  console.log('=== Scoped Persistence Migration Status ===');
  console.log('Owner:', owner);
  console.log('Migration completed:', migrated === 'true' ? 'Yes' : 'No');
  console.log('Migration flag key:', flagKey);
  console.log('\nLegacy keys (should be empty after migration):');
  if (currentLegacyKeys.length === 0) {
    console.log('  (none found - migration successful)');
  } else {
    for (const [key, value] of Object.entries(legacyKeys)) {
      console.log(`  ${key}:`, value ? 'HAS DATA' : '(empty)');
    }
  }
  console.log('\nScoped keys (should have data after migration):');
  const scopedKeyCount = Object.values(scopedKeys).filter(v => v).length;
  if (scopedKeyCount === 0) {
    console.log('  (none found)');
  } else {
    for (const [key, value] of Object.entries(scopedKeys)) {
      console.log(`  ${key}:`, value ? 'HAS DATA' : '(empty)');
    }
  }
  console.log('===========================================');
  /* eslint-enable no-console */
}

// Make debug function available in console
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__checkScopedPersistMigration = checkMigrationStatus;
}
